import { gql } from '@apollo/client'
import meetingCoreFragment from './fragments/meetingCore.fragment'
import meetingRestFragment from './fragments/meetingRest.fragment'
import { scoreGridFragment } from '../score-grid/fragments/scoreGridFragment'
import { SelfReflectionQuestionsFragment } from '../self-reflection/fragments/SelfReflectionQuestionsFragment'
import { ConclusionModalObjectiveFragment } from '../objectives/ConclusionModalObjectiveFragment'
import fullOneOffPaymentFragment from '../one-off-payment/fullOneOffPayment.fragment'
import fullObjectivePaymentFragment from '../objectivePayments/fullObjectivePayment.fragment'
import contractChangeFragment from '../contract/fragments/contractChange.fragment'
import targetBonusOfYearFragment from '../contract/fragments/targetBonusOfYear.fragment'
import latestContractFragment from '../contract/fragments/latestContract.fragment'
import meetingTargetsCalculationHelperFragment from './fragments/meetingTargetsCalculationHelper.fragment'
import appraisalSeasonFragment from './fragments/appraisalSeason.fragment'
import salaryOfYearFragment from '../contract/fragments/salaryOfYearFragment'
import employeeDisplayTinyInfoFragment from '../employee/fragments/employeeDisplayTinyInfo.fragment'

const FullMeeting = gql`
  fragment FullMeeting on Meeting {
    ...MeetingCore
    ...MeetingRest
    ...MeetingTargetsCalculationHelper
    ...AppraisalSeason
    selfReflectionAndEvaluationVisible
    activityListEnabled
  }
  ${meetingCoreFragment}
  ${meetingRestFragment}
  ${meetingTargetsCalculationHelperFragment}
  ${appraisalSeasonFragment}
`

const FullMeetingWithEvaluationAndSelfReflection = gql`
  fragment FullMeetingWithEvaluationAndSelfReflection on Meeting {
    ...MeetingCore
    ...MeetingRest
    ...MeetingTargetsCalculationHelper
    ...AppraisalSeason
    selfReflectionAndEvaluationVisible
    activityListEnabled
  }
  ${meetingCoreFragment}
  ${meetingRestFragment}
  ${meetingTargetsCalculationHelperFragment}
  ${appraisalSeasonFragment}
`

export const MEETING_WITH_CAREER_PATH_EVALUATION = gql`
  query MeetingWithCareerPathEvaluation($meetingId: HashedId!) {
    meeting(id: $meetingId) {
      ...FullMeetingWithEvaluationAndSelfReflection
    }
    careerPathEvaluationByMeetingId(meetingId: $meetingId) {
      salaryEvaluation
      bonusEvaluation
      summary
      salary
      targetBonus
    }
  }
  ${FullMeetingWithEvaluationAndSelfReflection}
`
export const MEETING_EMPLOYEE_PREPARATION = gql`
  query MeetingEmployeePreparation($meetingId: HashedId!) {
    meetingEmployeePreparation(id: $meetingId) {
      ...FullMeeting
    }
  }
  ${FullMeeting}
`
export const MEETING_MANAGER_PREPARATION_WITH_CAREER_PATH_EVALUATION = gql`
  query MeetingManagerPreparation($meetingId: HashedId!) {
    meetingManagerPreparation(id: $meetingId) {
      ...FullMeeting
    }
    careerPathEvaluationByMeetingId(meetingId: $meetingId) {
      salaryEvaluation
      bonusEvaluation
      summary
      salary
      targetBonus
    }
  }
  ${FullMeeting}
`

export const MEETING_PDF_STATUS = gql`
  query MeetingPdfStatus($meetingId: HashedId!) {
    meetingPdfStatus(id: $meetingId) {
      id
      conclusionPdfStatus
      preparationPdfStatus
    }
  }
`
export const APPRAISAL_STATUS_MODAL = gql`
  query appraisalStatusModal($meetingId: HashedId!, $employeeId: HashedId!, $country: Country!, $isAppraisalUpdate: Boolean = false) {
    fullMeetingPreview(meetingId: $meetingId, employeeId: $employeeId, isAppraisalUpdate: $isAppraisalUpdate) {
      meeting {
        ...FullMeeting
      }
      selfReflectionQuestions {
        ...SelfReflectionQuestion
      }
      scoreGrid {
        ...ScoreGridFragment
      }
      objectives {
        ...ConclusionModalObjectiveFragment
      }
      objectivePayment {
        ...FullObjectivePayment
      }
      oneOffPayment {
        ...FullOneOffPayment
      }
      contractChanges {
        ...ContractChange
      }
      targetBonusOfYear {
        ...TargetBonusOfYear
      }
      salaryOfYear {
        ...SalaryOfYear
      }
      latestContract {
        ...LatestContractDTO
      }
      targetCorrectionPayment @include(if: $isAppraisalUpdate) {
        id
        paymentDate
        status
        resultAmount
        comment
        referenceYear
        meetingId
        paymentDateValid
      }
    }
    branchOfficeByEmployee(employeeId: $employeeId) {
      code
      name
      timezone
      addressId
    }
    isBonusValidationEnabled(country: $country)
  }
  ${FullMeeting}
  ${scoreGridFragment}
  ${SelfReflectionQuestionsFragment}
  ${ConclusionModalObjectiveFragment}
  ${fullObjectivePaymentFragment}
  ${fullOneOffPaymentFragment}
  ${contractChangeFragment}
  ${targetBonusOfYearFragment}
  ${salaryOfYearFragment}
  ${latestContractFragment}
`
export const SCHEDULED_MEETINGS_BY_MANAGER = gql`
  query ScheduledMeetingsByManager($managerId: HashedId!, $year: Float!) {
    scheduledMeetingsByManager(managerId: $managerId, year: $year) {
      id
      date
      displayDate
      duration
      type
      status
    }
  }
`

export const APPRAISALS_WITH_FEEDBACK_BY_COUNTRY_AND_FILTERS = gql`
  query GetAppraisalsWithFeedbackByCountryAndFilters(
    $country: Country!
    $filters: HrSupportFeedbackTabFilter!
    $pagination: CursorPaginationMeetingInput!
  ) {
    getAppraisalsWithFeedbackByCountryAndFilters(country: $country, filters: $filters, pagination: $pagination) {
      edges {
        id
        date
        replyer {
          ...EmployeeDisplayTinyInfo
        }
        reviewer {
          ...EmployeeDisplayTinyInfo
        }
        discussedWith {
          ...EmployeeDisplayTinyInfo
        }
        status
        managerConfirmationDate
        employeeConfirmationDate
        conclusionPdf {
          fileName
          downloadToken
          status
        }
        hrFeedback
        hrResult
        hrDisputeComment
        meetingType
        appraisalSeason {
          id
          companyId
          country
          year
          fromDate
          toDate
        }
        companyName
      }
      endCursorMeetingId
      endCursorConfirmationDate
      hasNextPage
    }
  }
  ${employeeDisplayTinyInfoFragment}
`

export const APPRAISALS_WITH_FEEDBACK_COUNTER_BY_COUNTRY = gql`
  query GetAppraisalsWithFeedbackToResolveCounterByCountry($country: Country!) {
    getAppraisalsWithFeedbackToResolveCounterByCountry(country: $country)
  }
`
